/**
 * Room/property image helpers.
 *
 * `rooms.images` and `properties.images` are JSONB and arrive in several
 * shapes depending on how the row was written: a JSON string, an array, an
 * object keyed by index, and entries that are either a plain URL, a Storyblok
 * `f/...` path, or a `{ filename }` / `{ id }` object from the CMS sync.
 *
 * These helpers were lifted out of `Properties.tsx`, where they lived as local
 * closures, so the public room page can resolve a card thumbnail the same way
 * the listing grid does (COL-48).
 */

/**
 * Repair double-prefixed Storyblok URLs produced by the CMS sync, e.g.
 * `https://a.storyblok.com/f/https://a.storyblok.com/f/...`.
 */
export function fixStoryblokImageUrl(url: string): string {
  const double = "https://a.storyblok.com/f/https://a.storyblok.com/";
  if (url.startsWith(double)) {
    return "https://a.storyblok.com/" + url.slice(double.length);
  }
  return url;
}

/** Resolve a single image entry of any supported shape to an absolute URL. */
export function normalizeImageUrl(img: unknown): string | null {
  if (!img) return null;

  let imgStr = "";

  if (typeof img === "string") {
    imgStr = img;
  } else if (typeof img === "number") {
    imgStr = String(img);
  } else if (typeof img === "object") {
    if ("filename" in img && typeof img.filename === "string") {
      imgStr = img.filename;
    } else if ("id" in img) {
      imgStr = String((img as { id: unknown }).id);
    } else {
      return null;
    }
  } else {
    return null;
  }

  let out: string | null = null;
  if (imgStr.startsWith("http://") || imgStr.startsWith("https://")) {
    out = imgStr;
  } else if (imgStr.startsWith("f/")) {
    out = `https://a.storyblok.com/${imgStr}`;
  } else if (imgStr.trim()) {
    out = `https://a.storyblok.com/f/${imgStr}`;
  }
  return out ? fixStoryblokImageUrl(out) : null;
}

/** Coerce a JSONB images column into a list of entries. */
export function toImageArray(images: unknown): unknown[] {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  if (typeof images === "string") {
    try {
      const parsed = JSON.parse(images);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // A bare URL string rather than JSON.
      return [images];
    }
  }
  if (typeof images === "object") return Object.values(images);
  return [];
}

/** First usable image URL from a JSONB images column, or null. */
export function firstImageUrl(images: unknown): string | null {
  for (const entry of toImageArray(images)) {
    const url = normalizeImageUrl(entry);
    if (url) return url;
  }
  return null;
}

/**
 * Hero image for the room application page (COL-568): the room's own display
 * image (first of `rooms.images`), or null when the room has none, so the
 * caller can fall back to the static hero.
 */
export function roomHeroImage(images: unknown): string | null {
  return firstImageUrl(images);
}
